import * as vd from "virtual-dom";
import { merge as observableMerge, Subject } from "rxjs";
import { AbortInfra3DSDKError } from "../../error/AbortInfra3DSDKError";
import { EdgeDirection } from "../../graph/edge/EdgeDirection";
import { SequenceMode } from "./SequenceMode";
export class SequenceDOMRenderer {
    constructor(container) {
        this._container = container;
        this._minThresholdWidth = 320;
        this._maxThresholdWidth = 1480;
        this._minThresholdHeight = 240;
        this._maxThresholdHeight = 820;
        this._expandControls = true;
        this._mode = SequenceMode.Timeline;
        this._speed = 0.62;
        this._changingSpeed = false;
        this._index = null;
        this._changingPosition = false;
        this._mouseEnterDirection$ = new Subject();
        this._mouseLeaveDirection$ = new Subject();
        this._notifyChanged$ = new Subject();
        this._notifyChangingPositionChanged$ = new Subject();
        this._notifySpeedChanged$ = new Subject();
        this._notifyIndexChanged$ = new Subject();
    }
    get changed$() {
        return this._notifyChanged$;
    }
    get changingPositionChanged$() {
        return this._notifyChangingPositionChanged$;
    }
    get speed$() {
        return this._notifySpeedChanged$;
    }
    get index$() {
        return this._notifyIndexChanged$;
    }
    get mouseEnterDirection$() {
        return this._mouseEnterDirection$;
    }
    get mouseLeaveDirection$() {
        return this._mouseLeaveDirection$;
    }
    activate() {
        if (this._changingSubscription) {
            return;
        }
        this._changingSubscription = observableMerge(this._container.mouseService.documentMouseUp$).subscribe(() => {
            if (this._mode === SequenceMode.Playback && !this._changingSpeed) {
                this._mode = SequenceMode.Timeline;
                this._notifyChanged$.next(this);
            }
            if (this._changingSpeed) {
                this._changingSpeed = false;
            }
            if (this._changingPosition) {
                this._setChangingPosition(false);
            }
        });
    }
    deactivate() {
        if (!this._changingSubscription) {
            return;
        }
        this._changingSpeed = false;
        this._changingPosition = false;
        this._expandControls = false;
        this._mode = SequenceMode.Default;
        this._changingSubscription.unsubscribe();
        this._changingSubscription = null;
    }
    render(edgeStatus, configuration, containerWidth, speed, index, max, playEnabled, component, navigator, bridge) {
        if (configuration.visible === false) {
            return vd.h("div.mapillary-sequence-container", {}, []);
        }
        const containerHeight = configuration.height;
        const stepper = this._createStepper(edgeStatus, configuration, playEnabled, containerWidth, containerHeight, component, navigator, bridge);
        const controls = this._createSequenceControls(containerWidth, containerHeight, component, configuration);
        const playback = this._createPlaybackControls(containerWidth, speed);
        const timeline = this._createTimelineControls(containerWidth, containerHeight, index, max, configuration);
        return vd.h("div.mapillary-sequence-container", {
            style: {
                height: `${containerHeight}px`,
            },
        }, [stepper, controls, playback, timeline]);
    }
    getContainerWidth(size, configuration) {
        const minWidth = configuration.minWidth;
        let maxWidth = configuration.maxWidth;
        if (maxWidth < minWidth) {
            maxWidth = minWidth;
        }
        const relativeWidth = (size.width - this._minThresholdWidth) / (this._maxThresholdWidth - this._minThresholdWidth);
        const relativeHeight = (size.height - this._minThresholdHeight) / (this._maxThresholdHeight - this._minThresholdHeight);
        const coeff = Math.max(0, Math.min(1, Math.min(relativeWidth, relativeHeight)));
        return minWidth + coeff * (maxWidth - minWidth);
    }
    _createPositionInput(index, max, removewidth) {
        this._index = index;
        const onPosition = (e) => {
            this._index = Number(e.target.value);
            this._notifyIndexChanged$.next(this._index);
        };
        const onStart = (e) => {
            e.stopPropagation();
            this._setChangingPosition(true);
        };
        const onMove = (e) => {
            if (this._changingPosition === true) {
                e.stopPropagation();
            }
        };
        const onKeyDown = (e) => {
            if ([HTMLInputElement, HTMLTextAreaElement, HTMLSelectElement].some((instance) => e.target instanceof instance)) {
                return;
            }
            if (e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "ArrowUp") {
                e.preventDefault();
            }
        };
        const positionInputProperties = {
            max: max != null ? max : 1,
            min: 0,
            onchange: onPosition,
            oninput: onPosition,
            onkeydown: onKeyDown,
            onmousedown: onStart,
            onmousemove: onMove,
            ontouchmove: onMove,
            ontouchstart: onStart,
            type: "range",
            value: index != null ? index : 0,
        };
        const disabled = index == null || max == null || max <= 1;
        if (disabled) {
            positionInputProperties.disabled = "true";
        }
        const positionInput = vd.h("input.mapillary-sequence-position", positionInputProperties, []);
        const positionContainerClass = disabled
            ? ".mapillary-sequence-position-container-inactive"
            : ".mapillary-sequence-position-container";
        return vd.h("div" + positionContainerClass, [positionInput]);
    }
    _createSpeedInput(speed, removewidth) {
        this._speed = speed;
        const onSpeed = (e) => {
            this._speed = Number(e.target.value) / 1000;
            this._notifySpeedChanged$.next(this._speed);
        };
        const onStart = (e) => {
            this._changingSpeed = true;
            e.stopPropagation();
        };
        const onMove = (e) => {
            if (this._changingSpeed === true) {
                e.stopPropagation();
            }
        };
        const onKeyDown = (e) => {
            if ([HTMLInputElement, HTMLTextAreaElement, HTMLSelectElement].some((instance) => e.target instanceof instance)) {
                return;
            }
            if (e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "ArrowUp") {
                e.preventDefault();
            }
        };
        const speedInput = vd.h("input.mapillary-sequence-speed", {
            max: 1000,
            min: 0,
            onchange: onSpeed,
            oninput: onSpeed,
            onkeydown: onKeyDown,
            onmousedown: onStart,
            onmousemove: onMove,
            ontouchmove: onMove,
            ontouchstart: onStart,
            type: "range",
            value: 1000 * speed,
            ondblclick: (e) => {
                this._speed = 0.62;
                this._notifySpeedChanged$.next(this._speed);
            },
        }, []);
        return vd.h("div.mapillary-sequence-speed-container", [speedInput]);
    }
    _createPlaybackControls(containerWidth, speed) {
        if (this._mode !== SequenceMode.Playback) {
            return vd.h("div.mapillary-sequence-playback", []);
        }
        const speedInput = this._createSpeedInput(speed, containerWidth);
        const playbackChildren = [speedInput];
        return vd.h("div.mapillary-sequence-playback", playbackChildren);
    }
    _createPlayingButton(nextKey, prevKey, playEnabled, configuration, component) {
        // FIXME
        let canPlay = (configuration.direction === EdgeDirection.Next && nextKey != null) ||
            (configuration.direction === EdgeDirection.Prev && prevKey != null);
        canPlay = canPlay && playEnabled;
        const onclick = configuration.playing
            ? () => {
                component.stop();
            }
            : canPlay
                ? () => {
                    component.play();
                }
                : null;
        const buttonProperties = { onclick: onclick };
        const iconClass = configuration.playing
            ? "mapillary-sequence-icon-stop"
            : canPlay
                ? "mapillary-sequence-icon-play"
                : "mapillary-sequence-icon-play-inactive";
        const iconProperties = { className: iconClass };
        if (configuration.direction === EdgeDirection.Prev) {
            iconProperties.style = {
                transform: "rotate(180deg) translate(55%, 55%)",
            };
        }
        const icon = vd.h("div.mapillary-sequence-icon", iconProperties, []);
        const buttonClass = canPlay ? "mapillary-sequence-play" : "mapillary-sequence-play-inactive";
        const tooltipProperties = { className: "mapillary-sequence-tooltiptext" };
        const tooltip = vd.h("div.infra3dsdk-tooltip", tooltipProperties, configuration.locale.play);
        return vd.h("div." + buttonClass, buttonProperties, [icon, tooltip]);
    }
    _createSequenceControls(containerWidth, containerHeight, component, configuration) {
        const fastIconClassName = this._mode === SequenceMode.Playback
            ? ".mapillary-sequence-fast-icon-gray.mapillary-sequence-icon-visible"
            : ".mapillary-sequence-fast-icon";
        const fastIcon = vd.h("div" + fastIconClassName, []);
        const playbackProperties = {
            onclick: () => {
                this._mode = this._mode === SequenceMode.Playback ? SequenceMode.Timeline : SequenceMode.Playback;
                this._notifyChanged$.next(this);
            },
        };
        const tooltipProperties = { className: "mapillary-sequence-tooltiptext" };
        const tooltip = vd.h("div.infra3dsdk-tooltip", tooltipProperties, configuration.locale.speed);
        const playback = vd.h("div.mapillary-sequence-playback-button", playbackProperties, [
            fastIcon,
            tooltip,
        ]);
        const className = ".mapillary-sequence-controls" + (this._expandControls ? ".mapillary-sequence-controls-expanded" : "");
        return vd.h("div" + className, {
            style: {
                height: `${containerHeight}px`,
            },
        }, [playback]);
    }
    _createSequenceArrows(nextKey, prevKey, containerWidth, configuration, navigator) {
        const nextProperties = {
            onclick: nextKey != null
                ? () => {
                    navigator.moveToKey$(nextKey).subscribe(undefined, (error) => {
                        if (!(error instanceof AbortInfra3DSDKError)) {
                            console.error(error);
                        }
                    });
                }
                : null,
            onmouseenter: () => {
                this._mouseEnterDirection$.next(EdgeDirection.Next);
            },
            onmouseleave: () => {
                this._mouseLeaveDirection$.next(EdgeDirection.Next);
            },
        };
        const prevProperties = {
            onclick: prevKey != null
                ? () => {
                    navigator.moveToKey$(prevKey).subscribe(undefined, (error) => {
                        if (!(error instanceof AbortInfra3DSDKError)) {
                            console.error(error);
                        }
                    });
                }
                : null,
            onmouseenter: () => {
                this._mouseEnterDirection$.next(EdgeDirection.Prev);
            },
            onmouseleave: () => {
                this._mouseLeaveDirection$.next(EdgeDirection.Prev);
            },
        };
        const nextClass = this._getStepClassName(EdgeDirection.Next, nextKey, configuration.highlightKey);
        const prevClass = this._getStepClassName(EdgeDirection.Prev, prevKey, configuration.highlightKey);
        const nextIcon = vd.h("div.mapillary-sequence-icon", []);
        const prevIcon = vd.h("div.mapillary-sequence-icon", []);
        const tooltipProperties = { className: "mapillary-sequence-tooltiptext" };
        const tooltip_fwd = vd.h("div.infra3dsdk-tooltip", tooltipProperties, configuration.locale.forward);
        const tooltip_bwd = vd.h("div.infra3dsdk-tooltip", tooltipProperties, configuration.locale.backward);
        return [
            vd.h("div." + prevClass, prevProperties, [prevIcon, tooltip_bwd]),
            vd.h("div." + nextClass, nextProperties, [nextIcon, tooltip_fwd]),
        ];
    }
    _createStepper(edgeStatus, configuration, playEnabled, containerWidth, containerHeight, component, navigator, bridge) {
        let nextKey = null;
        let prevKey = null;
        for (const edge of edgeStatus.edges) {
            if (edge.data.direction === EdgeDirection.Next) {
                nextKey = edge.to;
            }
            if (edge.data.direction === EdgeDirection.Prev) {
                prevKey = edge.to;
            }
        }
        if (!prevKey && bridge) {
            if (bridge.assetsCached)
                prevKey = bridge.key;
        }
        if (!nextKey && bridge) {
            if (bridge.assetsCached)
                nextKey = bridge.key;
        }
        const playingButton = this._createPlayingButton(nextKey, prevKey, playEnabled, configuration, component);
        const buttons = this._createSequenceArrows(nextKey, prevKey, containerWidth, configuration, navigator);
        buttons.splice(1, 0, playingButton);
        const switchIcon = vd.h("div.mapillary-sequence-switch-icon.mapillary-sequence-icon-visible", []);
        const direction = configuration.direction === EdgeDirection.Next ? EdgeDirection.Prev : EdgeDirection.Next;
        const playing = configuration.playing;
        const switchButtonProperties = {
            onclick: () => {
                if (!playing) {
                    component.setDirection(direction);
                }
            },
        };
        const tooltipProperties = { className: "mapillary-sequence-tooltiptext" };
        const tooltip = vd.h("div.infra3dsdk-tooltip", tooltipProperties, configuration.locale.direction);
        const switchButtonClassName = configuration.playing
            ? ".mapillary-sequence-switch-button-inactive"
            : ".mapillary-sequence-switch-button";
        const switchButton = vd.h("div" + switchButtonClassName, switchButtonProperties, [
            switchIcon,
            tooltip,
        ]);
        buttons.push(switchButton);
        const containerProperties = {
            oncontextmenu: (event) => {
                event.preventDefault();
            },
            style: {
                height: `${containerHeight}px`,
            },
        };
        return vd.h("div.mapillary-sequence-stepper", containerProperties, buttons);
    }
    _createTimelineControls(containerWidth, containerHeight, index, max, configuration) {
        if (!configuration.showtimeline)
            return vd.h("div.mapillary-sequence-timeline", []);
        const positionInput = this._createPositionInput(index, max, containerWidth * 2);
        return vd.h("div.mapillary-sequence-timeline", {
            style: {
                height: `${containerHeight}px`,
            },
        }, [positionInput]);
    }
    _getStepClassName(direction, key, highlightKey) {
        let className = direction === EdgeDirection.Next ? "mapillary-sequence-step-next" : "mapillary-sequence-step-prev";
        if (key == null) {
            className += "-inactive";
        }
        else {
            if (highlightKey === key) {
                className += "-highlight";
            }
        }
        return className;
    }
    _setChangingPosition(value) {
        this._changingPosition = value;
        this._notifyChangingPositionChanged$.next(value);
    }
}
//# sourceMappingURL=SequenceDOMRenderer.js.map